import { GOOGLE_MAPS_API_KEY } from "../env";

/**
 * Idempotent loader for the Maps JavaScript API (#44). Coordinates-only usage: hybrid
 * imagery is rendered live and never persisted or exported. Rejects cleanly when no key
 * is set so callers fail open to a placeholder rather than crashing. Generic: reused by
 * both generator and solar placement.
 *
 * Resolution is gated on Google's `callback` (fired only once the API is fully ready,
 * including `google.maps.importLibrary`). Resolving on the <script> `onload` instead
 * would race: with `loading=async` the base script attaches `importLibrary` AFTER
 * onload. Callers obtain individual classes via `google.maps.importLibrary("maps"|
 * "marker"|"geocoding")`, so no `libraries=` param is needed here.
 */
let loadPromise: Promise<typeof google.maps> | null = null;

const CALLBACK = "__ksGoogleMapsReady__";

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error("VITE_GOOGLE_MAPS_API_KEY is not set"));
      return;
    }
    if (typeof google !== "undefined" && typeof google.maps?.importLibrary === "function") {
      resolve(google.maps);
      return;
    }
    (window as unknown as Record<string, () => void>)[CALLBACK] = () => resolve(google.maps);
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      v: "weekly",
      loading: "async",
      callback: CALLBACK,
    });
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      loadPromise = null; // allow a retry after a transient network failure
      reject(new Error("Failed to load the Google Maps JavaScript API"));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** Test seam — clears the module-level cache between tests. */
export function resetGoogleMapsLoader(): void {
  loadPromise = null;
}
